/**
 * Quantization and field (de)serialization. (§2.8)
 *
 * ── On Math.* in this file ─────────────────────────────────────────────────
 *
 * /sim may not call Math.sin/cos/atan2/pow (see math/vec3.ts) because the sim
 * must be bit-identical between the server's and the client's *simulations*.
 * /net is downstream of that guarantee and does not participate in it: the
 * server quantizes and the client dequantizes, and the two never compare
 * transcendental results with each other. Math.round/floor/sqrt/abs are all
 * exactly specified by IEEE-754 / ECMA-262 anyway. Nothing here feeds a value
 * back into the sim except through a snapshot, which is lossy by construction.
 *
 * ── On the field widths below ──────────────────────────────────────────────
 *
 * §2.8 fixes the widths that matter for the budget (position, rotation,
 * velocity, animation) and those live in sim/constants.ts as QUANT_*. The rest
 * of the wire needs a width for every field, and those widths are the PACKET
 * LAYOUT, not game feel — they are not leva-tunable and they do not belong in
 * the sim's tunables file (which is frozen). They live here, named and cited,
 * never inlined at a call site.
 *
 * CHANGING ANY WIDTH OR BOUND BELOW IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION
 * (§12.6) or clients will decode garbage and blame the physics.
 */

import type { Quat } from '../sim/types.js';
import {
  QUANT_ANGVEL_MAX,
  QUANT_ANGVEL_MIN,
  QUANT_ANIM_BLEND_BITS,
  QUANT_ANIM_ENUM_BITS,
  QUANT_POS_BITS,
  QUANT_POS_MAX_X,
  QUANT_POS_MAX_Y,
  QUANT_POS_MAX_Z,
  QUANT_POS_MIN_X,
  QUANT_POS_MIN_Y,
  QUANT_POS_MIN_Z,
  QUANT_QUAT_BITS,
  QUANT_VEL_BITS,
  QUANT_VEL_MAX,
  QUANT_VEL_MIN,
  STAMINA_MAX,
} from '../sim/constants.js';
import { PI, wrapAngle } from '../math/vec3.js';

// ---------------------------------------------------------------------------
// Wire field widths not fixed by §2.8's QUANT_* block
// ---------------------------------------------------------------------------

/**
 * Angular velocity. constants.ts declares the RANGE (QUANT_ANGVEL_MIN/MAX,
 * ±150 rad/s) and its comment specifies "16-bit per axis" but exports no
 * QUANT_ANGVEL_BITS. Rather than inline a 16 or edit the frozen constants file,
 * we reuse the velocity width, which is the same 16 that comment names.
 */
export const WIRE_ANGVEL_BITS = QUANT_VEL_BITS;

/**
 * Player facing. Players are upright, so a single yaw beats a quaternion:
 * 12 bits over [-π, π] is 0.0015 rad ≈ 0.09°, far below what is visible on a
 * turning body, at 20 bits/player/snapshot less than smallest-three would cost.
 * The ball, which has a real orientation, uses smallest-three (§2.8).
 */
export const WIRE_YAW_BITS = 12;

/** Stamina drives the fatigue speed cap (§3.3); 8 bits over 0..100 is 0.4 units. */
export const WIRE_STAMINA_BITS = 8;

/** Any normalized scalar: [-1,1] axes, [0,1] factors. 8 bits ≈ analog-stick resolution. */
export const WIRE_UNIT_BITS = 8;

/** SkillMove enum, 0..8 (§4.6). */
export const WIRE_SKILL_BITS = 4;

/** TacticalCommand enum, 0..4 (§4.3). */
export const WIRE_TACTICAL_BITS = 3;

/** FoulSeverity enum, 0..4 (§5.3). */
export const WIRE_SEVERITY_BITS = 3;

/** MatchPhase enum, 0..15 (§5.1). */
export const WIRE_PHASE_BITS = 4;

/** RestartStage enum, 0..2 (§5.1). */
export const WIRE_RESTART_STAGE_BITS = 2;

/**
 * A PlayerId reference, encoded as id+1 so the pervasive "or -1" sentinel costs
 * nothing extra: 0 = none, 1..MAX_PLAYERS = a player. 6 bits covers 0..63.
 */
export const WIRE_PLAYER_REF_BITS = 6;

/** A Team reference, encoded as team+1: 0 = none, 1 = A, 2 = B. */
export const WIRE_TEAM_REF_BITS = 2;

/**
 * Charge accumulators. The client charges and the server validates (§2.9), so
 * the wire only has to carry a *claim*; the bound is generous and the server
 * clamps it against PASS/SHOT_CHARGE_MAX_S regardless.
 */
export const WIRE_CHARGE_MAX_S = 2;

// ---------------------------------------------------------------------------
// Snapshot header + match-block widths (§2.8, §5)
//
// These live here for the same reason the enum widths above do: they are packet
// LAYOUT, not game feel, and snapshot.ts must not inline them. They sit next to
// WIRE_PHASE_BITS / WIRE_*_REF_BITS because they encode the same MatchState the
// snapshot's match block carries.
// ---------------------------------------------------------------------------

/** Sim tick / input sequence counters. 32 bits ≈ 828 days at 60Hz. (§2.2) */
export const WIRE_TICK_BITS = 32;
export const WIRE_SEQ_BITS = 32;

/** Match half, 1 or 2. (§5.5) */
export const WIRE_HALF_BITS = 2;

/**
 * Elapsed ticks in the current half. 2^20 at 60Hz ≈ 291 min — covers a full
 * 45-minute half plus any conceivable stoppage without truncation. (§5.5)
 */
export const WIRE_CLOCK_BITS = 20;

/** Accumulated stoppage ticks (injuries/subs/goals/VAR/cards). (§5.5) */
export const WIRE_ADDED_TIME_BITS = 16;

/** Goals per team, 0..127. */
export const WIRE_SCORE_BITS = 7;

/** Advantage-window countdown in ticks; ADVANTAGE_WINDOW_S=3 → ≤180 ticks. (§5.3) */
export const WIRE_ADVANTAGE_BITS = 9;

// ---------------------------------------------------------------------------
// Scalar quantization
// ---------------------------------------------------------------------------

/**
 * Map `value` in [min,max] onto an integer in [0, 2^bits - 1].
 *
 * Out-of-range values CLAMP rather than wrap. Wrapping would teleport a player
 * from one touchline to the other on a single bad frame; clamping pins them to
 * the edge of the quantization volume, which is both visible and harmless.
 */
export function quantizeFloat(value: number, min: number, max: number, bits: number): number {
  const maxLevel = bits === 32 ? 4294967295 : (1 << bits) - 1;
  const range = max - min;
  if (range <= 0) return 0;
  let t = (value - min) / range;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  // NaN would survive the comparisons above and poison Math.round; pin it to min.
  if (!(t >= 0)) t = 0;
  return Math.round(t * maxLevel);
}

/** Inverse of quantizeFloat. Error is at most half a quantization step. */
export function dequantizeFloat(q: number, min: number, max: number, bits: number): number {
  const maxLevel = bits === 32 ? 4294967295 : (1 << bits) - 1;
  if (maxLevel === 0) return min;
  return min + (q / maxLevel) * (max - min);
}

/** Size of one quantization step — the precision guarantee, for tests and docs. */
export function quantizationStep(min: number, max: number, bits: number): number {
  const maxLevel = bits === 32 ? 4294967295 : (1 << bits) - 1;
  return (max - min) / maxLevel;
}

// ---------------------------------------------------------------------------
// Position — 16-bit per axis over the bounded pitch volume (§2.8)
// ±60m / ±10m / ±40m → 1.83mm / 0.31mm / 1.22mm steps.
// ---------------------------------------------------------------------------

export function quantizePosX(v: number): number {
  return quantizeFloat(v, QUANT_POS_MIN_X, QUANT_POS_MAX_X, QUANT_POS_BITS);
}
export function quantizePosY(v: number): number {
  return quantizeFloat(v, QUANT_POS_MIN_Y, QUANT_POS_MAX_Y, QUANT_POS_BITS);
}
export function quantizePosZ(v: number): number {
  return quantizeFloat(v, QUANT_POS_MIN_Z, QUANT_POS_MAX_Z, QUANT_POS_BITS);
}
export function dequantizePosX(q: number): number {
  return dequantizeFloat(q, QUANT_POS_MIN_X, QUANT_POS_MAX_X, QUANT_POS_BITS);
}
export function dequantizePosY(q: number): number {
  return dequantizeFloat(q, QUANT_POS_MIN_Y, QUANT_POS_MAX_Y, QUANT_POS_BITS);
}
export function dequantizePosZ(q: number): number {
  return dequantizeFloat(q, QUANT_POS_MIN_Z, QUANT_POS_MAX_Z, QUANT_POS_BITS);
}

// ---------------------------------------------------------------------------
// Velocity / angular velocity — 16-bit per axis (§2.8)
// ---------------------------------------------------------------------------

export function quantizeVel(v: number): number {
  return quantizeFloat(v, QUANT_VEL_MIN, QUANT_VEL_MAX, QUANT_VEL_BITS);
}
export function dequantizeVel(q: number): number {
  return dequantizeFloat(q, QUANT_VEL_MIN, QUANT_VEL_MAX, QUANT_VEL_BITS);
}
export function quantizeAngVel(v: number): number {
  return quantizeFloat(v, QUANT_ANGVEL_MIN, QUANT_ANGVEL_MAX, WIRE_ANGVEL_BITS);
}
export function dequantizeAngVel(q: number): number {
  return dequantizeFloat(q, QUANT_ANGVEL_MIN, QUANT_ANGVEL_MAX, WIRE_ANGVEL_BITS);
}

// ---------------------------------------------------------------------------
// Misc scalars
// ---------------------------------------------------------------------------

/** Yaw is wrapped, not clamped — an angle just outside [-π,π] means the same thing. */
export function quantizeYaw(v: number): number {
  return quantizeFloat(wrapAngle(v), -PI, PI, WIRE_YAW_BITS);
}
export function dequantizeYaw(q: number): number {
  return dequantizeFloat(q, -PI, PI, WIRE_YAW_BITS);
}

export function quantizeStamina(v: number): number {
  return quantizeFloat(v, 0, STAMINA_MAX, WIRE_STAMINA_BITS);
}
export function dequantizeStamina(q: number): number {
  return dequantizeFloat(q, 0, STAMINA_MAX, WIRE_STAMINA_BITS);
}

/** Signed normalized scalar in [-1,1] — movement axes, lift bias. */
export function quantizeUnitSigned(v: number): number {
  return quantizeFloat(v, -1, 1, WIRE_UNIT_BITS);
}
export function dequantizeUnitSigned(q: number): number {
  return dequantizeFloat(q, -1, 1, WIRE_UNIT_BITS);
}

/** Unsigned normalized scalar in [0,1] — anim blend, excitement. */
export function quantizeUnit(v: number): number {
  return quantizeFloat(v, 0, 1, WIRE_UNIT_BITS);
}
export function dequantizeUnit(q: number): number {
  return dequantizeFloat(q, 0, 1, WIRE_UNIT_BITS);
}

export function quantizeCharge(v: number): number {
  return quantizeFloat(v, 0, WIRE_CHARGE_MAX_S, WIRE_UNIT_BITS);
}
export function dequantizeCharge(q: number): number {
  return dequantizeFloat(q, 0, WIRE_CHARGE_MAX_S, WIRE_UNIT_BITS);
}

/** Animation blend time, 0..1 normalized. 8 bits per §2.8. */
export function quantizeAnimBlend(v: number): number {
  return quantizeFloat(v, 0, 1, QUANT_ANIM_BLEND_BITS);
}
export function dequantizeAnimBlend(q: number): number {
  return dequantizeFloat(q, 0, 1, QUANT_ANIM_BLEND_BITS);
}

/** Encode a PlayerId (or -1) as a 0-based ref. See WIRE_PLAYER_REF_BITS. */
export function encodePlayerRef(id: number): number {
  return id < 0 ? 0 : id + 1;
}
export function decodePlayerRef(ref: number): number {
  return ref === 0 ? -1 : ref - 1;
}
export function encodeTeamRef(team: number): number {
  return team < 0 ? 0 : team + 1;
}
export function decodeTeamRef(ref: number): number {
  return ref === 0 ? -1 : ref - 1;
}

// ---------------------------------------------------------------------------
// Rotation — smallest-three quaternion compression, 32 bits total (§2.8)
// ---------------------------------------------------------------------------

/**
 * A unit quaternion has 3 degrees of freedom, so one component is redundant.
 * Drop the LARGEST one (its reconstruction via sqrt is then least sensitive to
 * the others' quantization error) and send the other three. Because the dropped
 * component is the largest of four unit-norm components, it is at least 1/2, and
 * the three we send are each bounded by 1/√2 — which is why they can use the
 * full 10 bits over that tighter range instead of over [-1,1].
 */
const QUAT_INDEX_BITS = 2; // 4 components → 2 bits. Structural, not tunable.
/** (32 - 2) / 3 = 10 bits per sent component. Derived from QUANT_QUAT_BITS. */
const QUAT_COMPONENT_BITS = (QUANT_QUAT_BITS - QUAT_INDEX_BITS) / 3;
const QUAT_COMPONENT_LEVELS = (1 << QUAT_COMPONENT_BITS) >>> 0;
/** 1/√2 — the mathematical bound on a non-largest unit-quaternion component. */
const QUAT_COMPONENT_RANGE = Math.SQRT1_2;

/**
 * Pack a quaternion into exactly QUANT_QUAT_BITS (32) bits:
 * [largestIndex:2][a:10][b:10][c:10].
 *
 * The quaternion is negated when the largest component is negative — q and -q
 * are the same rotation, so this costs nothing and lets the decoder recover the
 * dropped component as +sqrt(...) with no sign bit.
 */
export function packQuatSmallestThree(x: number, y: number, z: number, w: number): number {
  let nx = x;
  let ny = y;
  let nz = z;
  let nw = w;

  // Defensive normalize: a drifted quaternion would otherwise break the 1/√2
  // bound and clamp components, silently distorting the rotation.
  const len = Math.sqrt(x * x + y * y + z * z + w * w);
  if (len > 1e-9) {
    const inv = 1 / len;
    nx *= inv;
    ny *= inv;
    nz *= inv;
    nw *= inv;
  } else {
    nx = 0;
    ny = 0;
    nz = 0;
    nw = 1;
  }

  const ax = nx < 0 ? -nx : nx;
  const ay = ny < 0 ? -ny : ny;
  const az = nz < 0 ? -nz : nz;
  const aw = nw < 0 ? -nw : nw;

  let index = 0;
  let largestAbs = ax;
  if (ay > largestAbs) {
    index = 1;
    largestAbs = ay;
  }
  if (az > largestAbs) {
    index = 2;
    largestAbs = az;
  }
  if (aw > largestAbs) {
    index = 3;
    largestAbs = aw;
  }

  let largest: number;
  switch (index) {
    case 0: largest = nx; break;
    case 1: largest = ny; break;
    case 2: largest = nz; break;
    default: largest = nw; break;
  }
  if (largest < 0) {
    nx = -nx;
    ny = -ny;
    nz = -nz;
    nw = -nw;
  }

  let a: number;
  let b: number;
  let c: number;
  switch (index) {
    case 0: a = ny; b = nz; c = nw; break;
    case 1: a = nx; b = nz; c = nw; break;
    case 2: a = nx; b = ny; c = nw; break;
    default: a = nx; b = ny; c = nz; break;
  }

  const qa = quantizeFloat(a, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);
  const qb = quantizeFloat(b, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);
  const qc = quantizeFloat(c, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);

  // Arithmetic rather than shifts: index<<30 would hit the int32 sign bit.
  // Max value is exactly 2^32-1, so this fills the 32-bit budget precisely.
  let packed = index;
  packed = packed * QUAT_COMPONENT_LEVELS + qa;
  packed = packed * QUAT_COMPONENT_LEVELS + qb;
  packed = packed * QUAT_COMPONENT_LEVELS + qc;
  return packed >>> 0;
}

/** Inverse of packQuatSmallestThree. Writes into `out`; allocation-free. */
export function unpackQuatSmallestThree(packed: number, out: Quat): Quat {
  let p = packed >>> 0;
  const qc = p % QUAT_COMPONENT_LEVELS;
  p = Math.floor(p / QUAT_COMPONENT_LEVELS);
  const qb = p % QUAT_COMPONENT_LEVELS;
  p = Math.floor(p / QUAT_COMPONENT_LEVELS);
  const qa = p % QUAT_COMPONENT_LEVELS;
  p = Math.floor(p / QUAT_COMPONENT_LEVELS);
  const index = p % 4;

  const a = dequantizeFloat(qa, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);
  const b = dequantizeFloat(qb, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);
  const c = dequantizeFloat(qc, -QUAT_COMPONENT_RANGE, QUAT_COMPONENT_RANGE, QUAT_COMPONENT_BITS);

  // The dropped component is the largest and was forced positive before packing.
  const sq = 1 - a * a - b * b - c * c;
  const d = sq <= 0 ? 0 : Math.sqrt(sq);

  switch (index) {
    case 0: out.x = d; out.y = a; out.z = b; out.w = c; break;
    case 1: out.x = a; out.y = d; out.z = b; out.w = c; break;
    case 2: out.x = a; out.y = b; out.z = d; out.w = c; break;
    default: out.x = a; out.y = b; out.z = c; out.w = d; break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Animation — 6-bit enum + 8-bit normalized blend time (§2.8)
// ---------------------------------------------------------------------------

/** AnimState fits QUANT_ANIM_ENUM_BITS; asserted in serialize.test.ts. */
export const WIRE_ANIM_ENUM_BITS = QUANT_ANIM_ENUM_BITS;
export const WIRE_ANIM_BLEND_BITS = QUANT_ANIM_BLEND_BITS;
